import { createReducer } from '@reduxjs/toolkit'
import cloneDeep from 'lodash/cloneDeep'
import findKey from 'lodash/findKey'

import { userLogout } from '@/redux/auth/actions'
import { ScheduledExercise, ScheduledExerciseSettings } from '@/types'
import {
  addMultipleScheduledExercisesFailed,
  addMultipleScheduledExercisesRequest,
  addMultipleScheduledExercisesSuccess,
  assignScheduleFailed,
  assignScheduleRequest,
  assignScheduleSuccess,
  deleteScheduledExercisesByIdsFailed,
  deleteScheduledExercisesByIdsRequest,
  deleteScheduledExercisesByIdsSuccess,
  deleteScheduleFailed,
  deleteScheduleRequest,
  deleteScheduleSuccess,
  editScheduledExercisesByIdsFailed,
  editScheduledExercisesByIdsRequest,
  editScheduledExercisesByIdsSuccess,
  editScheduledExerciseSettingsByIdsFailed,
  editScheduledExerciseSettingsByIdsRequest,
  editScheduledExerciseSettingsByIdsSuccess,
  editScheduleFailed,
  editScheduleRequest,
  editScheduleSuccess,
  getSchedulesFailed,
  getSchedulesRequest,
  getSchedulesSuccess,
  getSingleScheduleSuccess,
  resetSchedules
} from './actions'
import type { SchedulesModel } from './types'

interface IScheduleState {
  loading: boolean
  scheduledExercisesSettingsLoading: boolean
  scheduledExercisesLoading: boolean
  schedulesData: SchedulesModel[]
}

interface IAssignScheduleState {
  loading: boolean
}

const initialState: IScheduleState = {
  loading: false,
  scheduledExercisesSettingsLoading: false,
  scheduledExercisesLoading: false,
  schedulesData: []
}

const initialStateAssignSchedule: IAssignScheduleState = {
  loading: false
}

function getScheduleKey (schedules: SchedulesModel[], scheduleId: number) {
  const key = findKey(
    schedules,
    (schedule: SchedulesModel) => scheduleId === schedule.id
  )

  if (!key) return -1

  return parseInt(key)
}

const schedulesReducer = createReducer(initialState, builder => {
  builder
    .addCase(getSchedulesRequest, state => {
      state.loading = true
    })
    .addCase(getSchedulesSuccess, (state, action) => {
      state.loading = false
      state.schedulesData = action.payload.schedulesData
    })
    .addCase(getSchedulesFailed, state => {
      state.loading = false
      state.schedulesData = []
    })
    .addCase(getSingleScheduleSuccess, (state, action) => {
      const scheduleKey = getScheduleKey(state.schedulesData, action.payload.id)
      if (scheduleKey === -1) {
        state.schedulesData = [...state.schedulesData, action.payload]
      } else {
        state.schedulesData[scheduleKey] = action.payload
      }
    })
    .addCase(deleteScheduleRequest, state => {
      state.loading = true
    })
    .addCase(deleteScheduleSuccess, (state, action) => {
      state.loading = false
      state.schedulesData = state.schedulesData.filter(
        scheduleData => scheduleData.id !== action.payload.id
      )
    })
    .addCase(deleteScheduleFailed, state => {
      state.loading = false
    })
    .addCase(editScheduleRequest, state => {
      state.loading = true
    })
    .addCase(editScheduleSuccess, (state, action) => {
      const scheduleKey = getScheduleKey(state.schedulesData, action.payload.id)
      if (scheduleKey === -1) return

      state.schedulesData[scheduleKey] = action.payload
      state.loading = false
    })
    .addCase(editScheduleFailed, state => {
      state.loading = false
    })
    .addCase(editScheduledExerciseSettingsByIdsRequest, state => {
      state.scheduledExercisesSettingsLoading = true
    })
    .addCase(editScheduledExerciseSettingsByIdsFailed, state => {
      state.scheduledExercisesSettingsLoading = false
    })
    .addCase(editScheduledExerciseSettingsByIdsSuccess, (state, action) => {
      const { value, textId, exercisesIds, scheduleId } = action.payload

      const scheduleKey = getScheduleKey(state.schedulesData, scheduleId)
      if (scheduleKey === -1) return

      const clonedSchedulesState = cloneDeep(state.schedulesData)

      exercisesIds.forEach(id => {
        const scheduledExerciseKey = findKey(
          state.schedulesData[scheduleKey]?.exercises,
          (ex: ScheduledExercise) => ex.scheduledExerciseDbId === id
        )
        if (!scheduledExerciseKey) return

        const scheduledExerciseSettingKey = findKey(
          state.schedulesData[scheduleKey]?.exercises?.[
            parseInt(scheduledExerciseKey)
          ]?.settings,
          (item: ScheduledExerciseSettings) => item.textId === textId
        )
        if (!scheduledExerciseSettingKey) return

        clonedSchedulesState[scheduleKey].exercises[
          parseInt(scheduledExerciseKey)
        ].settings[parseInt(scheduledExerciseSettingKey)].value = value
      })

      state.schedulesData = clonedSchedulesState
      state.scheduledExercisesSettingsLoading = false
    })
    .addCase(deleteScheduledExercisesByIdsRequest, state => {
      state.scheduledExercisesLoading = true
    })
    .addCase(deleteScheduledExercisesByIdsSuccess, (state, action) => {
      const { scheduledExerciseIds, scheduleId } = action.payload

      const scheduleKey = getScheduleKey(state.schedulesData, scheduleId)
      if (scheduleKey === -1) return

      let clonedExercises = cloneDeep(
        state.schedulesData[scheduleKey].exercises
      )

      clonedExercises = clonedExercises.filter(
        scheduledExercises =>
          !scheduledExerciseIds.includes(
            scheduledExercises.scheduledExerciseDbId
          )
      )

      state.schedulesData[scheduleKey].exercises = clonedExercises

      state.scheduledExercisesLoading = false
    })
    .addCase(deleteScheduledExercisesByIdsFailed, state => {
      state.scheduledExercisesLoading = false
    })
    .addCase(editScheduledExercisesByIdsFailed, state => {
      state.scheduledExercisesLoading = false
    })
    .addCase(editScheduledExercisesByIdsRequest, state => {
      state.scheduledExercisesLoading = true
    })
    .addCase(editScheduledExercisesByIdsSuccess, (state, action) => {
      const { scheduledExercisesData, id: scheduleId } = action.payload

      const scheduleKey = getScheduleKey(state.schedulesData, scheduleId)
      if (scheduleKey === -1) return

      const clonedScheduleExercises = cloneDeep(
        state.schedulesData[scheduleKey].exercises
      )

      scheduledExercisesData.forEach(({ id, ...scheduledExerciseData }) => {
        const scheduledExerciseKey = findKey(
          state.schedulesData[scheduleKey]?.exercises,
          (ex: ScheduledExercise) => ex.scheduledExerciseDbId === id
        )
        if (!scheduledExerciseKey) return

        clonedScheduleExercises[parseInt(scheduledExerciseKey)] = {
          ...clonedScheduleExercises[parseInt(scheduledExerciseKey)],
          ...scheduledExerciseData
        }
      })

      state.schedulesData[scheduleKey].exercises = clonedScheduleExercises
      state.scheduledExercisesLoading = false
    })
    .addCase(addMultipleScheduledExercisesRequest, state => {
      state.scheduledExercisesLoading = true
    })
    .addCase(addMultipleScheduledExercisesFailed, state => {
      state.scheduledExercisesLoading = false
    })
    .addCase(addMultipleScheduledExercisesSuccess, (state, action) => {
      const { scheduledExercises, scheduleId } = action.payload

      const scheduleKey = getScheduleKey(state.schedulesData, scheduleId)
      if (scheduleKey === -1) return

      state.schedulesData[scheduleKey].exercises = [
        ...state.schedulesData[scheduleKey].exercises,
        ...scheduledExercises
      ]

      state.scheduledExercisesLoading = false
    })
    .addCase(userLogout, state => {
      state.loading = false
      state.scheduledExercisesSettingsLoading = false
      state.scheduledExercisesLoading = false
      state.schedulesData = []
    })
    .addCase(resetSchedules, state => {
      state.loading = false
      state.schedulesData = []
    })
})

const assignScheduleReducer = createReducer(
  initialStateAssignSchedule,
  builder => {
    builder
      .addCase(assignScheduleRequest, state => {
        state.loading = true
      })
      .addCase(assignScheduleSuccess, state => {
        state.loading = false
      })
      .addCase(assignScheduleFailed, state => {
        state.loading = false
      })
  }
)

export { assignScheduleReducer, schedulesReducer }
